import { SecureContentsId } from 'shared-client/secure-contents-id';
import { HtmlElementView } from './html-element-view.js';
import { MainView, PassphraseFormView } from './interfaces.js';
import { SecureContents } from './secure-contents.js';

export class PassphraseForm extends HtmlElementView implements PassphraseFormView {
  constructor(
    readonly form: HTMLFormElement,
    readonly input: HTMLInputElement,
    readonly wordList: HTMLElement,
    readonly resetButton: HTMLButtonElement,
    readonly mainView: MainView,
  ) {
    super(form);

    this.bindPassphraseInput();
    this.bindFormSubmit();
    this.bindResetButton();
  }
  private readonly setTimeout = window.setTimeout.bind(window);

  private bindPassphraseInput() {
    this.input.addEventListener('focus', () => {
      this.input.removeAttribute('placeholder');
    });
    this.input.addEventListener('blur', () => {
      if (!this.input.value) {
        this.input.setAttribute('placeholder', 'Enter word...');
      }
    });
  }

  private invalidInput() {
    const parent = this.input.parentElement;
    if (!parent) {
      return;
    }
    this.addInvalid(this.input);
  }

  private addInvalid(element: HTMLElement) {
    element.classList.add('invalid');
    element.addEventListener('transitionend', () => {
      element.classList.remove('invalid');
    }, { once: true });
  }

  private bindFormSubmit() {
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const word = this.input.value.toLowerCase().trim();
      if (!word) {
        return;
      }
      const result = await this.mainView.passphraseHandler.addWord(word);
      if (result.event === 'invalid') {
        this.invalidInput();
        return;
      }
      this.input.value = '';
      this.renderWords(false);
      if (result.event === 'incomplete') {
        return;
      }
      const secureContentsId = SecureContentsId.fromParts(
        this.mainView.passphraseHandler.getPassphrase(),
        this.mainView.salt,
      );

      try {
        this.setFormInputEnablement(false);

        const dek = await this.mainView.apiClient.getDek(
          await secureContentsId.getIdHash(),
        );

        const secureContents = await SecureContents.fromEncrypted(
          secureContentsId,
          dek.encryptedDek,
        );

        this.renderWords(true);

        this.setTimeout(() => {
          this.mainView.showDownloadDetails({
            contentType: dek.contentType,
            fileName: dek.fileName,
            fileSize: dek.fileSize,
            downloadUrl: dek.url,
            secureContents: secureContents,
            fileType: dek.fileType,
          });
        }, 750);
      } catch (err) {
        this.setFormInputEnablement(true);
        throw err;
      }
    });
  }

  private setFormInputEnablement(enabled: boolean) {
    this.input.disabled = !enabled;
    const button = this.form.querySelector('button[type="submit"]');
    if (button && 'disabled' in button) {
      button.disabled = !enabled;
    }
    this.resetButton.disabled = !enabled;
    if (enabled) {
      this.form.classList.remove('loading');
    } else {
      this.form.classList.add('loading');
    }
  }

  private bindResetButton() {
    this.resetButton.addEventListener('click', () => {
      this.resetForm();
    });
  }

  resetForm(): void {
    this.mainView.passphraseHandler.reset();
    this.input.value = '';
    this.renderWords();
  }

  private renderWords(isValid: boolean = false) {
    const words = this.mainView.passphraseHandler.getWords();
    if (words.length < this.wordList.children.length) {
      // Animate and remove excess elements
      const excessElements = Array.from(this.wordList.children).slice(words.length);
      excessElements.forEach(el => {
        el.remove();
      });
    }

    for (let index = 0; index < words.length; index++) {
      const word = words[index];
      let element = this.wordList.querySelector(`#word-${index}`) as HTMLDivElement | null;
      if (!element) {
        element = this.renderWord(word, index);
        // Add transition-in class for animation
        element.classList.add('transition-in');
        this.wordList.appendChild(element);
        // Remove the class after the transition ends
        element.addEventListener('transitionend', function() {
          this.classList.remove('transition-in');
        }, { once: true });

        // Force reflow to ensure transition triggers
        void element.offsetWidth;
      }
    }
    if (isValid) {
      this.wordList.classList.add('valid');
    }
  }

  private renderWord(word: string, index: number): HTMLDivElement {
    const div = document.createElement('div');
    div.id = `word-${index}`;
    div.classList.add('passphrase-row', 'passphrase-list-row');
    const text = word.toLowerCase().padEnd(10, ' ');
    for (const c of text) {
      const span = document.createElement('span');
      span.classList.add('passphrase-cell');
      if (c === ' ') {
        span.classList.add('empty');
      } else {
        span.textContent = c;
      }
      div.appendChild(span);
    }
    return div;
  }
}
